import React, { useEffect, useId, useMemo } from "react";
import { fonts } from "../theme";

export function cardStyle(theme) {
  return {
    background: theme.surface,
    border: `1px solid ${theme.line}`,
    borderRadius: 14,
    padding: "16px 18px",
  };
}

export function Kpi({ label, value, hint, theme, onClick, active }) {
  const clickable = typeof onClick === "function";
  const style = {
    ...cardStyle(theme),
    width: "100%",
    textAlign: "left",
    cursor: clickable ? "pointer" : "default",
    borderColor: active ? theme.accent : theme.line,
    boxShadow: active ? `0 0 0 1px ${theme.accent}` : "none",
    padding: "18px 20px",
  };
  const inner = (
    <>
      <div className="text-uppercase fw-bold mb-1" style={{ color: theme.textMute, letterSpacing: "0.06em", fontSize: 12 }}>{label}</div>
      <div className="fw-bold" style={{ fontFamily: fonts.display, fontSize: 32, color: theme.text, lineHeight: 1.15 }}>{value ?? "—"}</div>
      {hint ? <div className="small mt-1" style={{ color: theme.textMute, fontSize: 12 }}>{hint}</div> : null}
      {clickable ? <div className="fw-bold mt-2" style={{ color: theme.accent, fontSize: 13 }}>{active ? "Close list ↑" : "View list →"}</div> : null}
    </>
  );
  if (!clickable) return <div style={style}>{inner}</div>;
  return (
    <button type="button" className="border-0" style={style} onClick={onClick}>
      {inner}
    </button>
  );
}

export function UserPieChart({ slices = [], theme, size = 135, onSliceClick, onCenterClick, activeKey }) {
  const total = slices.reduce((sum, item) => sum + (Number(item.value) || 0), 0);
  if (!total) {
    return <div className="small text-center py-3" style={{ color: theme.textMute }}>No user data yet.</div>;
  }
  const ranges = [];
  let acc = 0;
  const stops = slices.map((item) => {
    const start = (acc / total) * 100;
    acc += Number(item.value) || 0;
    const end = (acc / total) * 100;
    ranges.push({ item, start: start / 100, end: end / 100 });
    return `${item.color} ${start}% ${end}%`;
  }).join(", ");
  const hole = Math.round(size * 0.55);
  const clickable = typeof onSliceClick === "function";

  function hitSlice(event) {
    const rect = event.currentTarget.getBoundingClientRect();
    const dx = event.clientX - (rect.left + rect.width / 2);
    const dy = event.clientY - (rect.top + rect.height / 2);
    const dist = Math.sqrt(dx * dx + dy * dy);
    const outer = rect.width / 2;
    const inner = (hole / size) * outer;
    if (dist <= inner) {
      if (typeof onCenterClick === "function") onCenterClick();
      return;
    }
    if (!clickable || dist > outer) return;
    let deg = Math.atan2(dx, -dy) * (180 / Math.PI);
    if (deg < 0) deg += 360;
    const pct = deg / 360;
    const hit = ranges.find((range) => pct >= range.start && pct < range.end) || ranges.filter((range) => range.item.value > 0).slice(-1)[0];
    if (hit) onSliceClick(hit.item);
  }

  return (
    <div className="d-flex align-items-center justify-content-center gap-3 gap-xl-4 h-100 flex-grow-1 flex-wrap py-1">
      <div
        role={clickable ? "img" : undefined}
        aria-label="Users by role. Click a color to open that list."
        onClick={hitSlice}
        style={{
          width: size,
          height: size,
          borderRadius: "50%",
          background: `conic-gradient(${stops})`,
          flexShrink: 0,
          position: "relative",
          cursor: clickable || typeof onCenterClick === "function" ? "pointer" : "default",
          boxShadow: `0 0 0 5px ${theme.line}`,
        }}
      >
        <div
          className="d-flex flex-column align-items-center justify-content-center"
          style={{
            position: "absolute",
            inset: (size - hole) / 2,
            borderRadius: "50%",
            background: theme.surface,
            pointerEvents: "none",
          }}
        >
          <div className="fw-bold" style={{ fontFamily: fonts.display, fontSize: 20, color: theme.text, lineHeight: 1.1 }}>{total}</div>
          <div style={{ fontSize: 10, color: theme.textMute, textTransform: "uppercase", letterSpacing: "0.06em", lineHeight: 1, marginTop: 2 }}>Total</div>
        </div>
      </div>
      <div className="d-flex flex-column justify-content-center" style={{ minWidth: 150, flex: "1 1 150px", maxWidth: 260 }}>
        {slices.map((item) => {
          const active = activeKey && (activeKey === item.key || activeKey === item.role);
          const rowContent = (
            <div
              className="d-flex align-items-center justify-content-between px-2.5 py-1.5 rounded mb-1"
              style={{
                background: active ? `${item.color}22` : `${theme.line}25`,
                border: `1px solid ${active ? item.color : "transparent"}`,
                fontSize: 12,
                width: "100%",
              }}
            >
              <span className="d-flex align-items-center gap-2 text-truncate" style={{ color: theme.text, fontWeight: 500 }}>
                <span style={{ width: 8, height: 8, borderRadius: 99, background: item.color, flexShrink: 0 }} />
                <span className="text-truncate">{item.label}</span>
              </span>
              <strong className="ms-2" style={{ color: theme.text, fontFamily: fonts.display || fonts.body, fontSize: 13 }}>{item.value}</strong>
            </div>
          );
          if (!clickable) {
            return <div key={item.label}>{rowContent}</div>;
          }
          return (
            <button
              key={item.label}
              type="button"
              className="w-100 p-0 border-0 text-start bg-transparent"
              onClick={() => onSliceClick(item)}
              style={{ cursor: "pointer" }}
            >
              {rowContent}
            </button>
          );
        })}
      </div>
    </div>
  );
}

export function AdminModal({ theme, title, open, onClose, children }) {
  useEffect(() => {
    if (!open) return undefined;
    const onKey = (event) => {
      if (event.key === "Escape") onClose();
    };
    document.addEventListener("keydown", onKey);
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = previousOverflow;
    };
  }, [open, onClose]);

  if (!open) return null;
  return (
    <div
      className="position-fixed top-0 start-0 w-100 h-100 d-flex align-items-start justify-content-center"
      style={{ zIndex: 1080, background: "rgba(0,0,0,0.55)", padding: "4vh 16px", overflowY: "auto" }}
      onClick={onClose}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-label={title}
        style={{
          ...cardStyle(theme),
          width: "min(1100px, 100%)",
          maxHeight: "88vh",
          display: "flex",
          flexDirection: "column",
          boxShadow: "0 24px 80px rgba(0,0,0,0.35)",
        }}
        onClick={(event) => event.stopPropagation()}
      >
        <div className="d-flex align-items-center justify-content-between gap-2 mb-3">
          <div className="fw-bold" style={{ fontFamily: fonts.display, fontSize: 18 }}>{title}</div>
          <button type="button" className="btn btn-sm btn-outline-secondary" onClick={onClose}>Close</button>
        </div>
        <div style={{ overflow: "auto", minHeight: 120 }}>{children}</div>
      </div>
    </div>
  );
}

export function PageHeader({ title, subtitle, theme, onRefresh, busy, children }) {
  return (
    <div className="d-flex flex-wrap align-items-center justify-content-between gap-2 mb-2">
      <div>
        <h1 className="h5 fw-bold mb-0" style={{ fontFamily: fonts.display }}>{title}</h1>
        {subtitle ? <p className="small mb-0" style={{ color: theme.textMute, fontSize: 12 }}>{subtitle}</p> : null}
      </div>
      <div className="d-flex flex-wrap gap-2">
        {children}
        {onRefresh ? (
          <button type="button" className="btn btn-sm py-1 px-2" style={{ background: theme.accent, color: "#fff", border: 0, fontSize: 12 }} onClick={onRefresh} disabled={busy}>
            Refresh
          </button>
        ) : null}
      </div>
    </div>
  );
}

export function formatDate(value) {
  if (!value) return "—";
  try {
    return new Date(value).toLocaleString();
  } catch (e) {
    return String(value);
  }
}

export function StatusPill({ value, theme }) {
  const raw = String(value || "unknown").toLowerCase();
  const tone = raw.includes("active") || raw.includes("healthy") || raw.includes("verified") || raw.includes("published") || raw.includes("completed") || raw.includes("success")
    ? "#10B981"
    : raw.includes("pending") || raw.includes("review") || raw.includes("warning") || raw.includes("flag")
      ? "#F59E0B"
      : raw.includes("error") || raw.includes("fail") || raw.includes("reject") || raw.includes("suspend") || raw.includes("hidden") || raw.includes("invalid")
        ? "#EF4444"
        : theme.textMute;
  return (
    <span className="small fw-semibold px-2 py-1 rounded-pill" style={{ background: `${tone}22`, color: tone, textTransform: "capitalize" }}>
      {value || "—"}
    </span>
  );
}

export function AdminTable({ columns, rows, theme, empty = "No records found." }) {
  const wrap = { overflowX: "auto", border: `1px solid ${theme.line}`, borderRadius: 12 };
  const th = { padding: "10px 12px", fontSize: 12, color: theme.textMute, textAlign: "left", borderBottom: `1px solid ${theme.line}`, whiteSpace: "nowrap" };
  const td = { padding: "10px 12px", fontSize: 13, color: theme.text, borderBottom: `1px solid ${theme.line}`, verticalAlign: "top" };
  return (
    <div style={wrap}>
      <table className="w-100" style={{ borderCollapse: "collapse" }}>
        <thead>
          <tr>{columns.map((col) => <th key={col.key} style={th}>{col.label}</th>)}</tr>
        </thead>
        <tbody>
          {!rows || rows.length === 0 ? (
            <tr><td style={td} colSpan={columns.length}>{empty}</td></tr>
          ) : rows.map((row, idx) => (
            <tr key={row.key || (row.role != null && row.id != null ? `${row.role}-${row.id}` : row.id) || idx}>
              {columns.map((col) => (
                <td key={col.key} style={td}>{col.render ? col.render(row) : (row[col.key] ?? "—")}</td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export function TrendBars({ items, theme, labelKey = "day", valueKey = "total" }) {
  const data = Array.isArray(items) ? items.slice().reverse() : [];
  const max = Math.max(1, ...data.map((item) => Number(item[valueKey]) || 0));
  if (!data.length) {
    return <div className="small" style={{ color: theme.textMute }}>No trend data yet.</div>;
  }
  return (
    <div className="d-flex align-items-end gap-1" style={{ height: 120 }}>
      {data.map((item, idx) => {
        const value = Number(item[valueKey]) || 0;
        const height = Math.max(4, Math.round((value / max) * 100));
        return (
          <div key={`${item[labelKey]}-${idx}`} className="flex-grow-1 d-flex flex-column align-items-center justify-content-end" style={{ minWidth: 0 }}>
            <div title={`${item[labelKey]}: ${value}`} style={{ width: "100%", maxWidth: 18, height, background: theme.accent, borderRadius: 4, opacity: 0.85 }} />
          </div>
        );
      })}
    </div>
  );
}

function ymdLocal(date) {
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(date.getDate()).padStart(2, "0")}`;
}

export function padTrendDays(items, days = 14) {
  const raw = Array.isArray(items) ? items : [];
  const map = new Map();
  raw.forEach((item) => {
    const day = String(item.day || item.date || "").slice(0, 10);
    if (day) map.set(day, Number(item.total) || 0);
  });
  const out = [];
  const now = new Date();
  now.setHours(12, 0, 0, 0);
  for (let i = days - 1; i >= 0; i -= 1) {
    const d = new Date(now);
    d.setDate(now.getDate() - i);
    const key = ymdLocal(d);
    out.push({
      day: key,
      label: `${d.getDate()}/${d.getMonth() + 1}`,
      total: map.get(key) || 0,
    });
  }
  if (out.some((item) => item.total > 0)) return out;
  const fallback = [...map.entries()]
    .sort((a, b) => a[0].localeCompare(b[0]))
    .slice(-days)
    .map(([day, total]) => {
      const parts = day.split("-");
      return { day, total, label: `${Number(parts[2] || 0)}/${Number(parts[1] || 0)}` };
    });
  return fallback.length ? fallback : out;
}

function niceMax(value) {
  const n = Math.max(1, Number(value) || 1);
  if (n <= 4) return 4;
  const pow = 10 ** Math.floor(Math.log10(n));
  return Math.ceil(n / pow) * pow;
}

function smoothPath(points, closeY) {
  if (!points.length) return "";
  let d = `M ${points[0].x} ${points[0].y}`;
  for (let i = 1; i < points.length; i += 1) {
    const prev = points[i - 1];
    const curr = points[i];
    const cx = (prev.x + curr.x) / 2;
    d += ` C ${cx} ${prev.y}, ${cx} ${curr.y}, ${curr.x} ${curr.y}`;
  }
  if (closeY != null) {
    d += ` L ${points[points.length - 1].x} ${closeY} L ${points[0].x} ${closeY} Z`;
  }
  return d;
}

function ChartEmpty({ theme, height = 100 }) {
  return <div className="small d-flex align-items-center justify-content-center flex-grow-1" style={{ minHeight: height, color: theme.textMute, fontSize: 11 }}>No trend data yet.</div>;
}

export function ChartCard({ theme, title, accent, highlight, children }) {
  return (
    <div className="w-100 d-flex flex-column" style={{
      ...cardStyle(theme),
      padding: "8px 12px 6px",
      height: "100%",
      minHeight: 0,
      borderColor: highlight ? accent || theme.accent : theme.line,
      boxShadow: theme.key === "light" ? "0 8px 20px rgba(2,132,199,0.06)" : "0 8px 20px rgba(0,0,0,0.18)",
      borderLeft: `3px solid ${accent || theme.accent}`,
    }}>
      <div className="small text-uppercase fw-semibold mb-1" style={{ color: theme.textMute, letterSpacing: "0.05em", fontSize: 10 }}>{title}</div>
      <div className="flex-grow-1 d-flex flex-column" style={{ minHeight: 0 }}>{children}</div>
    </div>
  );
}

function ChartGrid({ theme, W, H, L, R, T, B, max }) {
  const innerH = H - T - B;
  const ticks = [0, 0.5, 1];
  return (
    <>
      {ticks.map((t) => {
        const y = T + innerH * (1 - t);
        return (
          <g key={t}>
            <line x1={L} x2={W - R} y1={y} y2={y} stroke={theme.line} strokeWidth="1" strokeDasharray="3 3" />
            <text x={L - 5} y={y + 3.5} textAnchor="end" fontSize="10" fill={theme.textMute}>{Math.round(max * t)}</text>
          </g>
        );
      })}
    </>
  );
}

function TrendSvg({ theme, data, color, variant = "line", height = 130 }) {
  const gid = useId().replace(/:/g, "");
  const W = 480;
  const H = height;
  const L = 30;
  const R = 12;
  const T = 10;
  const B = 22;
  const innerW = W - L - R;
  const innerH = H - T - B;
  const max = niceMax(Math.max(1, ...data.map((item) => Number(item.total) || 0)));
  const points = data.map((item, idx) => {
    const x = L + (data.length === 1 ? innerW / 2 : (idx / (data.length - 1)) * innerW);
    const y = T + innerH * (1 - ((Number(item.total) || 0) / max));
    return { ...item, x, y };
  });
  const barW = Math.max(6, (innerW / data.length) * 0.52);
  return (
    <svg viewBox={`0 0 ${W} ${H}`} width="100%" height="100%" style={{ flex: 1, minHeight: 0, display: "block" }} preserveAspectRatio="none" role="img">
      <defs>
        <linearGradient id={`${gid}-fill`} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor={color} stopOpacity={variant === "area" ? 0.38 : 0.2} />
          <stop offset="100%" stopColor={color} stopOpacity="0.01" />
        </linearGradient>
        <linearGradient id={`${gid}-bar`} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor={color} stopOpacity="0.95" />
          <stop offset="100%" stopColor={color} stopOpacity="0.55" />
        </linearGradient>
      </defs>
      <ChartGrid theme={theme} W={W} H={H} L={L} R={R} T={T} B={B} max={max} />
      {variant === "bar" ? points.map((point) => {
        const h = Math.max(3, T + innerH - point.y);
        return (
          <rect
            key={point.day}
            x={point.x - barW / 2}
            y={point.y}
            width={barW}
            height={h}
            rx="3"
            fill={`url(#${gid}-bar)`}
          >
            <title>{`${point.label}: ${point.total}`}</title>
          </rect>
        );
      }) : (
        <>
          <path d={smoothPath(points, T + innerH)} fill={`url(#${gid}-fill)`} />
          <path d={smoothPath(points)} fill="none" stroke={color} strokeWidth={variant === "area" ? 1.8 : 2} strokeLinejoin="round" />
          {variant === "line" ? points.map((point) => (
            <circle key={point.day} cx={point.x} cy={point.y} r="2.2" fill={theme.surface} stroke={color} strokeWidth="1.5">
              <title>{`${point.label}: ${point.total}`}</title>
            </circle>
          )) : null}
        </>
      )}
      {points.map((point, idx) => (idx % 2 === 0 || idx === points.length - 1) ? (
        <text key={`l-${point.day}`} x={point.x} y={H - 4} textAnchor="middle" fontSize="9.5" fill={theme.textMute}>{point.label}</text>
      ) : null)}
    </svg>
  );
}

export function LineTrendChart({ theme, items, color = "#38BDF8", height = 130 }) {
  const data = useMemo(() => padTrendDays(items), [items]);
  if (!data.some((item) => item.total > 0)) return <ChartEmpty theme={theme} height={height} />;
  return <TrendSvg theme={theme} data={data} color={color} variant="line" height={height} />;
}

export function BarTrendChart({ theme, items, color = "#818CF8", height = 130 }) {
  const data = useMemo(() => padTrendDays(items), [items]);
  if (!data.some((item) => item.total > 0)) return <ChartEmpty theme={theme} height={height} />;
  return <TrendSvg theme={theme} data={data} color={color} variant="bar" height={height} />;
}

export function AreaTrendChart({ theme, items, color = "#F59E0B", height = 130 }) {
  const data = useMemo(() => padTrendDays(items), [items]);
  if (!data.some((item) => item.total > 0)) return <ChartEmpty theme={theme} height={height} />;
  return <TrendSvg theme={theme} data={data} color={color} variant="area" height={height} />;
}

export function ComboTrendChart({ theme, bars = [], line = [], barColor = "#10B981", lineColor = "#38BDF8", height = 145 }) {
  const gid = useId().replace(/:/g, "");
  const barData = useMemo(() => padTrendDays(bars), [bars]);
  const lineData = useMemo(() => padTrendDays(line), [line]);
  const hasData = barData.some((item) => item.total > 0) || lineData.some((item) => item.total > 0);
  if (!hasData) return <ChartEmpty theme={theme} height={height} />;
  const W = 680;
  const H = height;
  const L = 30;
  const R = 12;
  const T = 10;
  const B = 22;
  const innerW = W - L - R;
  const innerH = H - T - B;
  const max = niceMax(Math.max(1, ...barData.map((item) => item.total), ...lineData.map((item) => item.total)));
  const barW = Math.max(6, (innerW / barData.length) * 0.45);
  const linePoints = lineData.map((item, idx) => {
    const x = L + (lineData.length === 1 ? innerW / 2 : (idx / (lineData.length - 1)) * innerW);
    const y = T + innerH * (1 - (item.total / max));
    return { ...item, x, y };
  });
  return (
    <div className="d-flex flex-column justify-content-between h-100 flex-grow-1" style={{ minHeight: 0 }}>
      <svg viewBox={`0 0 ${W} ${H}`} width="100%" height="100%" style={{ flex: 1, minHeight: 0, display: "block" }} preserveAspectRatio="none" role="img">
        <defs>
          <linearGradient id={`${gid}-bar`} x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor={barColor} stopOpacity="0.95" />
            <stop offset="100%" stopColor={barColor} stopOpacity="0.4" />
          </linearGradient>
        </defs>
        <ChartGrid theme={theme} W={W} H={H} L={L} R={R} T={T} B={B} max={max} />
        {barData.map((item, idx) => {
          const x = L + (barData.length === 1 ? innerW / 2 : (idx / (barData.length - 1)) * innerW);
          const y = T + innerH * (1 - (item.total / max));
          const h = Math.max(3, T + innerH - y);
          return (
            <rect key={item.day} x={x - barW / 2} y={y} width={barW} height={h} rx="3" fill={`url(#${gid}-bar)`}>
              <title>{`${item.label}: ${item.total}`}</title>
            </rect>
          );
        })}
        <path d={smoothPath(linePoints)} fill="none" stroke={lineColor} strokeWidth="2" strokeLinejoin="round" />
        {linePoints.map((point) => (
          <circle key={point.day} cx={point.x} cy={point.y} r="2.2" fill={theme.surface} stroke={lineColor} strokeWidth="1.5" />
        ))}
        {barData.map((item, idx) => (idx % 2 === 0 || idx === barData.length - 1) ? (
          <text key={`l-${item.day}`} x={L + (idx / Math.max(1, barData.length - 1)) * innerW} y={H - 4} textAnchor="middle" fontSize="9.5" fill={theme.textMute}>{item.label}</text>
        ) : null)}
      </svg>
      <div className="d-flex justify-content-end gap-3 pt-1 flex-shrink-0" style={{ fontSize: 11 }}>
        <span className="d-flex align-items-center gap-1.5" style={{ color: theme.textMute }}><span style={{ width: 8, height: 8, borderRadius: 2, background: barColor }} /> Applications</span>
        <span className="d-flex align-items-center gap-1.5" style={{ color: theme.textMute }}><span style={{ width: 12, height: 2, background: lineColor }} /> Registrations</span>
      </div>
    </div>
  );
}

export function HorzBarChart({ theme, items = [], onSelect, activeKey }) {
  const max = niceMax(Math.max(1, ...items.map((item) => Number(item.value) || 0)));
  if (!items.some((item) => Number(item.value) > 0)) return <ChartEmpty theme={theme} />;
  return (
    <div className="d-flex flex-column justify-content-center" style={{ minHeight: 148, gap: 10 }}>
      {items.map((item) => {
        const value = Number(item.value) || 0;
        const pct = Math.max(4, Math.round((value / max) * 100));
        const active = activeKey && (activeKey === item.key || activeKey === item.role);
        const inner = (
          <>
            <div className="d-flex justify-content-between mb-1">
              <span className="small" style={{ color: theme.text }}>{item.label}</span>
              <strong className="small">{value}</strong>
            </div>
            <div style={{ height: 8, borderRadius: 99, background: theme.line, overflow: "hidden" }}>
              <div style={{ width: `${pct}%`, height: "100%", borderRadius: 99, background: item.color || theme.accent }} />
            </div>
          </>
        );
        if (typeof onSelect !== "function") {
          return <div key={item.key || item.label}>{inner}</div>;
        }
        return (
          <button
            key={item.key || item.label}
            type="button"
            className="border-0 text-start"
            onClick={() => onSelect(item)}
            style={{
              background: active ? `${item.color}18` : "transparent",
              borderRadius: 8,
              padding: "2px 4px",
              cursor: "pointer",
            }}
          >
            {inner}
          </button>
        );
      })}
    </div>
  );
}

export function SnapshotColumns({ theme, items = [], onSelect, activeKey }) {
  const max = niceMax(Math.max(1, ...items.map((item) => Number(item.value) || 0)));
  const W = 420;
  const H = 148;
  const L = 12;
  const R = 12;
  const T = 18;
  const B = 28;
  const innerW = W - L - R;
  const innerH = H - T - B;
  const slot = innerW / Math.max(1, items.length);
  const barW = Math.min(42, slot * 0.46);
  return (
    <svg viewBox={`0 0 ${W} ${H}`} width="100%" height="148" preserveAspectRatio="none" role="img">
      {[0, 0.5, 1].map((t) => (
        <line key={t} x1={L} x2={W - R} y1={T + innerH * (1 - t)} y2={T + innerH * (1 - t)} stroke={theme.line} strokeWidth="1" />
      ))}
      {items.map((item, idx) => {
        const value = Number(item.value) || 0;
        const x = L + slot * idx + slot / 2;
        const h = Math.max(4, (value / max) * innerH);
        const y = T + innerH - h;
        const active = activeKey && activeKey === item.key;
        return (
          <g key={item.key || item.label} onClick={() => onSelect && onSelect(item)} style={{ cursor: onSelect ? "pointer" : "default" }}>
            <rect x={x - barW / 2} y={y} width={barW} height={h} rx="8" fill={item.color || theme.accent} opacity={active ? 1 : 0.88} />
            <text x={x} y={y - 6} textAnchor="middle" fontSize="10" fontWeight="700" fill={theme.text}>{value}</text>
            <text x={x} y={H - 8} textAnchor="middle" fontSize="9" fill={theme.textMute}>{item.label}</text>
          </g>
        );
      })}
    </svg>
  );
}

export function useOutletTheme() {
  return null;
}
